/**
 * Vanilla-DOM-Loader für das Widchat-Widget.
 *
 * Auf der Endkunden-Seite läuft *nur* dieses Mini-Bundle (keine React-, Supabase-,
 * AI-SDK-Tree-Shake-Kosten). Es zeigt sofort einen Launcher-Button an. Erst wenn
 * der Visitor das Widget öffnet — oder der Browser idle ist — wird das eigentliche
 * App-Bundle (`widchat-app.js`) nachgeladen, das den vollen Chat samt Realtime,
 * Cookie-Session und KI-Stream enthält.
 *
 * Der Embed-Snippet bleibt identisch:
 *   <script async src="https://widget.wid.chat/widchat.js" id="<embed_public_key>"></script>
 */

import {
  WIDCHAT_EMBED_LOADER_ATTR,
  WIDCHAT_EMBED_SCRIPT_FILENAME,
} from "@widchat/contracts";

type WidchatRuntime = {
  mount: (container: HTMLElement, embedPublicKey: string) => void;
};

declare global {
  interface Window {
    __widchat?: WidchatRuntime;
  }
}

const STYLE_TAG_ID = "widchat-loader-styles";
const HOST_ID = "widchat-host";
const APP_SCRIPT_ID = "widchat-app-script";
const LAUNCHER_CLASS = "widchat-loader-launcher";

const LAUNCHER_CSS = `
.${LAUNCHER_CLASS} {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 52px;
  height: 52px;
  border-radius: 999px;
  border: 0;
  background: #0f172a;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
  cursor: pointer;
  font: inherit;
  z-index: 2147483645;
  transition: transform 120ms ease, box-shadow 120ms ease;
}
.${LAUNCHER_CLASS}:hover { transform: translateY(-1px); box-shadow: 0 12px 28px rgba(15, 23, 42, 0.22); }
.${LAUNCHER_CLASS}:focus-visible { outline: 2px solid #60a5fa; outline-offset: 2px; }
.${LAUNCHER_CLASS}[data-loading="true"] { opacity: 0.7; cursor: progress; }
.${LAUNCHER_CLASS}[hidden] { display: none; }
`;

function injectLoaderStyles() {
  if (document.getElementById(STYLE_TAG_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_TAG_ID;
  style.textContent = LAUNCHER_CSS;
  document.head.appendChild(style);
}

function looksLikeEmbedPublicKey(value: string): boolean {
  return /^[a-f0-9]{8,64}$/i.test(value);
}

function readPublicKeyFromLoaderScript(loaderSrc: string): string {
  if (typeof document === "undefined") return "";
  const list = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"));
  // Bevorzugt das exakte Loader-Script (selbe absolute src).
  const exact = list.find((el) => el.src === loaderSrc);
  if (exact) {
    const id = exact.id?.trim() ?? "";
    if (looksLikeEmbedPublicKey(id)) return id;
  }
  // Fallback: passende Filename-Endung mit valider id.
  for (const el of list) {
    try {
      const base = el.src ? new URL(el.src, document.baseURI).pathname : "";
      if (
        base.endsWith(`/${WIDCHAT_EMBED_SCRIPT_FILENAME}`) ||
        base === `/${WIDCHAT_EMBED_SCRIPT_FILENAME}`
      ) {
        const id = el.id?.trim() ?? "";
        if (looksLikeEmbedPublicKey(id)) return id;
      }
    } catch {
      /* ignore */
    }
  }
  return "";
}

function getCurrentScriptSrc(): string | null {
  const cs = document.currentScript as HTMLScriptElement | null;
  if (cs?.src) return cs.src;
  // Fallback: das letzte widchat.js im Document.
  const fallback = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"))
    .reverse()
    .find((el) => {
      try {
        return new URL(el.src, document.baseURI).pathname.endsWith(`/${WIDCHAT_EMBED_SCRIPT_FILENAME}`);
      } catch {
        return false;
      }
    });
  return fallback?.src ?? null;
}

function appBundleUrl(loaderSrc: string): string {
  return loaderSrc.replace(/widchat\.js(\?.*)?$/, "widchat-app.js$1");
}

function ensureHost(): HTMLElement {
  let host = document.getElementById(HOST_ID);
  if (!host) {
    host = document.createElement("div");
    host.id = HOST_ID;
    document.body.appendChild(host);
  }
  return host;
}

function buildLauncher(onClick: () => void, accent = "#0f172a"): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = LAUNCHER_CLASS;
  btn.setAttribute("aria-label", "Chat öffnen");
  btn.style.background = accent;
  btn.innerHTML =
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/></svg>';
  btn.addEventListener("click", onClick, { once: true });
  return btn;
}

let appLoadingPromise: Promise<WidchatRuntime> | null = null;

function loadAppBundle(loaderSrc: string): Promise<WidchatRuntime> {
  if (window.__widchat) return Promise.resolve(window.__widchat);
  if (appLoadingPromise) return appLoadingPromise;

  appLoadingPromise = new Promise((resolve, reject) => {
    const fail = (message: string) => {
      // Reset shared promise so next click can genuinely retry.
      appLoadingPromise = null;
      document.getElementById(APP_SCRIPT_ID)?.remove();
      reject(new Error(message));
    };
    const resolveRuntime = () => {
      if (window.__widchat) {
        resolve(window.__widchat);
      } else {
        fail("Widchat-App ist geladen, exportiert aber kein Runtime-Objekt.");
      }
    };

    const existing = document.getElementById(APP_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      const readyState = (existing as HTMLScriptElement & { readyState?: string }).readyState;
      if (readyState === "complete") {
        resolveRuntime();
        return;
      }
      existing.addEventListener("load", resolveRuntime, { once: true });
      existing.addEventListener("error", () => fail("Widchat-App konnte nicht geladen werden."), {
        once: true,
      });
      return;
    }
    const script = document.createElement("script");
    script.id = APP_SCRIPT_ID;
    script.src = appBundleUrl(loaderSrc);
    script.async = true;
    script.crossOrigin = "anonymous";
    script.addEventListener("load", resolveRuntime, { once: true });
    script.addEventListener("error", () => fail("Widchat-App konnte nicht geladen werden."), {
      once: true,
    });
    document.head.appendChild(script);
  });

  return appLoadingPromise;
}

function showSetupError(message: string) {
  const host = ensureHost();
  host.innerHTML = "";
  const box = document.createElement("div");
  box.setAttribute("role", "alert");
  box.style.cssText =
    "position:fixed;bottom:24px;right:24px;max-width:360px;padding:12px 14px;border-radius:12px;background:#fff;color:#0f172a;border:1px solid #e2e8f0;font:13px/1.4 system-ui;box-shadow:0 8px 24px rgba(15,23,42,.18);z-index:2147483645";
  box.textContent = message;
  host.appendChild(box);
}

function bootstrap() {
  if (typeof document === "undefined") return;
  const loaderSrc = getCurrentScriptSrc();
  if (!loaderSrc) return; // SSR / unbekannter Lade-Kontext

  const embedPublicKey = readPublicKeyFromLoaderScript(loaderSrc);
  if (!embedPublicKey) {
    showSetupError(
      `Es fehlt der Embed-Public-Key. Setze das Attribut ${WIDCHAT_EMBED_LOADER_ATTR} auf dem Script-Tag.`,
    );
    return;
  }

  injectLoaderStyles();
  const host = ensureHost();

  let mounted = false;
  const mountApp = () => {
    launcher.dataset.loading = "true";
    loadAppBundle(loaderSrc)
      .then((runtime) => {
        mounted = true;
        launcher.hidden = true;
        runtime.mount(host, embedPublicKey);
      })
      .catch((err) => {
        delete launcher.dataset.loading;
        // Re-arm: nächster Klick versucht wieder.
        launcher.addEventListener("click", mountApp, { once: true });
        console.error("[widchat] App-Bundle laden fehlgeschlagen:", err);
      });
  };
  const launcher = buildLauncher(() => {
    if (mounted) return;
    mountApp();
  });

  host.appendChild(launcher);

  // Idle-Preload: Laden vorbereiten ohne zu mounten — Klick fühlt sich sofort an.
  const idle =
    (window as Window & { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback ??
    ((cb: () => void) => window.setTimeout(cb, 1500));
  idle(() => {
    void loadAppBundle(loaderSrc).catch(() => {
      /* Klick versucht nochmal */
    });
  });
}

bootstrap();
